import React, { useState, useRef, useEffect } from 'react';

interface SellerBadgeProps {
  username: string;
  positivePercentage: number;
  totalRatings: number;
  formattedCount: string;
  isTopRatedPlus?: boolean;
  hasFastShipping?: boolean;
  hasTracking?: boolean;
  returnsMoneyBack?: boolean;
  showFullBadge?: boolean;
}

const SellerBadge: React.FC<SellerBadgeProps> = ({
  username,
  positivePercentage,
  totalRatings,
  formattedCount,
  isTopRatedPlus = false,
  hasFastShipping = false,
  hasTracking = false,
  returnsMoneyBack = false,
  showFullBadge = true,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        setShowTooltip(false);
      }
    };

    if (showTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTooltip]);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Top Rated Plus Badge with Tooltip */}
      {isTopRatedPlus && (
        <div className="relative" ref={tooltipRef}>
          <button
            type="button"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            onClick={() => setShowTooltip(!showTooltip)}
            className="flex items-center space-x-1 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg px-3 py-1.5 hover:from-purple-600 hover:to-purple-700 transition shadow-sm"
          >
            <i className="ri-star-fill text-yellow-300"></i>
            <span className="font-semibold text-sm">Top Rated Plus</span>
          </button>

          {/* Tooltip Popup */}
          {showTooltip && (
            <div className="absolute z-50 mb-2 bottom-full left-0 w-64 bg-gray-800 text-white rounded-lg shadow-xl p-4">
              <div className="absolute -bottom-2 left-4 w-4 h-4 bg-gray-800 transform rotate-45"></div>
              <h3 className="font-bold text-lg mb-3 flex items-center">
                <i className="ri-star-fill text-yellow-300 mr-2"></i>
                Top Rated Plus
              </h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-green-400 mr-2 mt-0.5"></i>
                  <span>Sellers with highest buyer ratings</span>
                </li>
                {returnsMoneyBack && (
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-green-400 mr-2 mt-0.5"></i>
                    <span>Returns, money back</span>
                  </li>
                )}
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-green-400 mr-2 mt-0.5"></i>
                  <span>Ships in a business day with tracking</span>
                </li>
              </ul>
              <button
                onClick={() => setShowTooltip(false)}
                className="mt-3 text-xs text-gray-300 hover:text-white underline"
              >
                Learn More
              </button>
            </div>
          )}
        </div>
      )}

      {/* Additional badges if showing full badge */}
      {showFullBadge && (
        <>
          {hasTracking && (
            <div className="flex items-center space-x-1 bg-blue-50 border border-blue-200 rounded-lg px-2 py-1">
              <span className="text-blue-700 text-xs">📦 With tracking</span>
            </div>
          )}
          {returnsMoneyBack && (
            <div className="flex items-center space-x-1 bg-yellow-50 border border-yellow-200 rounded-lg px-2 py-1">
              <span className="text-yellow-700 text-xs">↩️ Returns, money back</span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SellerBadge;

